/* Warm exact, read-only PC quotes before a collection card opens. No saved prices replace fresh validation. */
(() => {
  'use strict';
  const script = document.currentScript;
  const storageKey = 'elite:pc-navigation:v2:' + (window.ElitePCBuildPricing?.cacheKey || 'legacy');
  const maxAge = 15000;
  const keys = new Set(['case', 'mobo', 'cpu', 'ram', 'gpu', 'psu', 'cooler', 'wifi', 'sata_ssd', 'sata_hdd', 'nvme_ssd', 'windows_key', 'kettle_lead']);
  const required = ['case', 'mobo', 'cpu', 'ram', 'gpu', 'psu', 'windows_key'];
  const signature = selections => JSON.stringify(Object.entries(selections).sort(([a], [b]) => a.localeCompare(b)));
  const fresh = startedAt => Number.isFinite(startedAt) && Date.now() >= startedAt && Date.now() - startedAt < maxAge;
  const valid = validation => validation?.success === true && validation.price?.currency === 'GBP'
    && typeof validation.price.amount === 'number' && Number.isFinite(validation.price.amount) && validation.price.amount > 0;
  const saveData = () => navigator.connection?.saveData === true;
  const pending = new Map();
  const queue = [];
  let active = 0, automaticCount = 0, assetsPrimed = false;
  let priceSource = null, priceText = null, priceData = null;

  function nativePrices() {
    const source = document.querySelector('[data-elite-collection-pc-pricing]');
    const text = source?.textContent || 'null';
    // Reuse the parsed catalogue while its DOM source is unchanged. A new
    // node or changed price/stock text always invalidates this cached parse.
    if (source !== priceSource || text !== priceText) {
      priceSource = source; priceText = text; priceData = null;
      try { priceData = JSON.parse(text)?.variantPrices || null; } catch {}
    }
    return priceData;
  }

  function parse(anchor) {
    let url;
    try { url = new URL(anchor?.getAttribute('href'), location.href); } catch { return null; }
    if (url.origin !== location.origin || !/(?:^|\/)products\/[^/]+\/?$/.test(url.pathname)
      || url.searchParams.getAll('pc').length !== 1 || !/^gaming-pc(?:-|$)/.test(url.searchParams.get('pc') || '')) return null;
    const selections = {};
    for (const [param, value] of url.searchParams) {
      if (/accessor/i.test(param)) return null;
      if (!/^(?:elite_|component_)/.test(param)) continue;
      const key = param.replace(/^(?:elite_|component_)/, '');
      if (!keys.has(key) || Object.hasOwn(selections, key) || !value || /^__no_/.test(value)) return null;
      selections[key] = value;
    }
    if (required.some(key => !selections[key])) return null;
    return { selections, path: url.pathname, signature: signature(selections) };
  }
  function fingerprint(selections) {
    try {
      const native = nativePrices();
      if (native?.currency !== 'GBP' || !native.bySku) return null;
      const selected = {};
      for (const sku of Object.values(selections)) {
        if (!Object.hasOwn(native.bySku, sku)) continue;
        const value = native.bySku[sku];
        if (!Array.isArray(value) || !Number.isSafeInteger(value[0]) || value[0] < 0 || typeof value[1] !== 'boolean') return null;
        selected[sku] = [value[0], value[1]];
      }
      return Object.keys(selected).length ? signature(selected) : null;
    } catch { return null; }
  }
  function stored() {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter(item => item?.version === 1 && fresh(item.startedAt)
        && typeof item.path === 'string' && typeof item.signature === 'string' && typeof item.nativeFingerprint === 'string'
        && item.result?.ok === true && valid(item.result.validation)).slice(-6) : [];
    } catch { return []; }
  }
  function remember(job, validation) {
    if (!fresh(job.startedAt) || !valid(validation)) return;
    const rows = stored().filter(item => item.path !== job.path || item.signature !== job.signature);
    rows.push({ version: 1, startedAt: job.startedAt, path: job.path, signature: job.signature,
      result: { ok: true, validation }, nativeFingerprint: job.nativeFingerprint });
    try { sessionStorage.setItem(storageKey, JSON.stringify(rows.slice(-6))); } catch { /* Storage is optional. */ }
  }
  function primeAssets() {
    if (assetsPrimed || saveData()) return;
    assetsPrimed = true;
    for (const href of new Set([script?.dataset.runtime, script?.dataset.priceRuntime].filter(Boolean))) {
      const link = document.createElement('link');
      // These scripts belong to the next product page, not this collection.
      link.rel = 'prefetch'; link.as = 'script'; link.href = href; link.fetchPriority = 'low';
      document.head.append(link);
    }
  }
  function run() {
    while (active < 2 && queue.length) {
      const job = queue.shift();
      const current = parse(job.anchor);
      if (saveData() || !job.anchor.isConnected || !current || current.path !== job.path || current.signature !== job.signature) {
        pending.delete(job.key); continue;
      }
      active++;
      job.startedAt = Date.now();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      (window.ElitePCQuoteTransport?.fetch || fetch)('/apps/pc-configurator?action=validate', {
        method: 'POST', cache: 'no-store', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ selections: job.selections, accessories: [], checkout: false })
      }).then(async response => {
        if (!response.ok) return;
        const validation = await response.json();
        if (fingerprint(job.selections) !== job.nativeFingerprint) return;
        remember(job, validation);
      }).catch(() => {}).finally(() => {
        clearTimeout(timer); pending.delete(job.key); active--; run();
      });
    }
  }
  function warm(anchor, intent = false) {
    if (saveData()) return false;
    const recipe = parse(anchor);
    if (!recipe) return false;
    const nativeFingerprint = fingerprint(recipe.selections);
    if (!nativeFingerprint) return false;
    primeAssets();
    const key = recipe.path + '|' + recipe.signature;
    if (pending.has(key)) {
      const queued = queue.indexOf(pending.get(key));
      if (intent && queued > 0) queue.unshift(...queue.splice(queued, 1));
      return true;
    }
    if (stored().some(item => item.path === recipe.path && item.signature === recipe.signature
      && item.nativeFingerprint === nativeFingerprint)) return true;
    const job = { ...recipe, key, anchor, nativeFingerprint };
    pending.set(key, job); intent ? queue.unshift(job) : queue.push(job); run();
    return true;
  }
  function start() {
    const root = document.querySelector('[data-elite-collection-grid]');
    if (!root || saveData()) return;
    const observed = new WeakSet(), near = new WeakSet(), automaticallyWarmed = new WeakSet();
    // IntersectionObserver already supplies rendered visibility. Avoid forcing
    // synchronous layout from a DOM mutation merely to warm a read-only quote.
    const visible = card => !card.hidden && !card.closest('[hidden]');
    const automatic = card => {
      if (automaticCount >= 3 || automaticallyWarmed.has(card) || !visible(card)
        || card.querySelector('[data-elite-pc-card-pricing]')?.dataset.elitePcPriceState !== 'ready') return;
      const anchor = [...card.querySelectorAll('a[href]')].find(item => parse(item));
      if (anchor && warm(anchor)) { automaticCount++; automaticallyWarmed.add(card); }
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) { near.add(entry.target); automatic(entry.target); }
        else near.delete(entry.target);
      }
    }, { rootMargin: '300px' }) : null;
    const scan = () => {
      if (automaticCount >= 3) { observer?.disconnect(); return; }
      for (const card of root.querySelectorAll('[data-elite-product-card]')) {
        if (observer) {
          if (!observed.has(card)) { observed.add(card); observer.observe(card); }
          if (near.has(card)) automatic(card);
        } else {
          const rect = card.getBoundingClientRect();
          if (rect.bottom > -300 && rect.top < innerHeight + 300) automatic(card);
        }
      }
    };
    let scanQueued = false;
    new MutationObserver(() => {
      if (scanQueued || automaticCount >= 3) return;
      scanQueued = true; requestAnimationFrame(() => { scanQueued = false; scan(); });
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'hidden', 'data-elite-pc-price-state'] });
    for (const event of ['pointerover', 'focusin', 'touchstart']) root.addEventListener(event, input => {
      const anchor = input.target?.closest?.('a[href]');
      if (anchor && root.contains(anchor)) warm(anchor, true);
    }, { passive: true });
    if (!observer) window.addEventListener('scroll', scan, { passive: true });
    scan();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
